/**
 * API Client for Niko Vibe
 * Handles all API communications with the backend
 */

import axios from 'axios';
import toast from 'react-hot-toast';

export const GUEST_CHECKOUT_TOKEN_KEY = 'guestCheckoutToken';

export function setGuestCheckoutToken(token) {
  if (token) sessionStorage.setItem(GUEST_CHECKOUT_TOKEN_KEY, token);
}

export function clearGuestCheckoutToken() {
  sessionStorage.removeItem(GUEST_CHECKOUT_TOKEN_KEY);
}

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const userToken = localStorage.getItem('token');
    const guestToken = sessionStorage.getItem(GUEST_CHECKOUT_TOKEN_KEY);
    const url = `${config.baseURL || ''}${config.url || ''}`;
    // Checkout/payment APIs must prefer the guest session token. A stale
    // localStorage login JWT otherwise shadows it and returns 401 mid-checkout.
    const isCheckoutApi = /\/api\/payments(\/|$)/.test(url)
      || /\/api\/tickets\/purchase/.test(url)
      || /\/api\/tickets\/[^/?]+(\/download)?$/.test(url);

    if (isCheckoutApi && guestToken) {
      config.headers.Authorization = `Bearer ${guestToken}`;
    } else if (userToken) {
      config.headers.Authorization = `Bearer ${userToken}`;
    } else if (guestToken) {
      config.headers.Authorization = `Bearer ${guestToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    const onCheckout = window.location.pathname.startsWith('/checkout')
      || window.location.pathname.startsWith('/payment/');
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      const skipRedirect = error.config?.skipAuthRedirect;
      const loginPath = '/login';
      const returnTo = window.location.pathname + window.location.search + window.location.hash;
      const loginUrl = returnTo && returnTo !== '/login'
        ? `${loginPath}?returnTo=${encodeURIComponent(returnTo)}`
        : loginPath;

      if (!onCheckout && !skipRedirect) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = loginUrl;
        }
      }
    }
    
    // Checkout page renders its own error UI — avoid duplicate toasts
    if (!onCheckout) {
      if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.');
      } else if (error.response?.status === 403) {
        toast.error(message || 'Access denied.');
      } else if (error.response?.status === 404) {
        toast.error('Resource not found.');
      } else if (message !== 'Network Error') {
        toast.error(message);
      }
    }
    
    const authError = new Error(message);
    authError.status = error.response?.status;
    return Promise.reject(authError);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials, { timeout: 45000 }),
  register: (userData) => api.post('/api/auth/register', userData, { timeout: 45000 }),
  getProfile: (options = {}) => api.get('/api/auth/profile', options),
  updateProfile: (data) => api.put('/api/auth/profile', data),
  changePassword: (data) => api.put('/api/auth/password', data),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }, { timeout: 45000 }),
  resetPassword: (data) => api.post('/api/auth/reset-password', data, { timeout: 45000 }),
};

// Events API
export const eventsAPI = {
  getEvents: (params = {}) => api.get('/api/events', { params }),
  getEvent: (id) => api.get(`/api/events/${id}`),
  getFeaturedEvents: () => api.get('/api/events/featured'),
  getEventsByCategory: (categoryId, params = {}) => 
    api.get(`/api/events/category/${categoryId}`, { params }),
  searchEvents: (params = {}) => api.get('/api/events/search', { params }),
  createEvent: (eventData) => api.post('/api/events', eventData),
  updateEvent: (id, eventData) => api.put(`/api/events/${id}`, eventData),
  deleteEvent: (id) => api.delete(`/api/events/${id}`),
};

// Tickets API
export const ticketsAPI = {
  getTickets: () => api.get('/api/tickets'),
  getTicket: (id) => api.get(`/api/tickets/${id}`),
  getTicketsForUser: (userId, params = {}) => api.get(`/api/tickets/user/${userId}`, { params }),
  purchaseTickets: (data) => api.post('/api/tickets/purchase', data, { timeout: 45000 }),
  validateTicket: (qrCode) => api.post('/api/tickets/validate', { qrCode }),
  downloadTicketPdf: async (ticketId) => {
    const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
    const userToken = localStorage.getItem('token');
    const guestToken = sessionStorage.getItem(GUEST_CHECKOUT_TOKEN_KEY);
    const token = userToken || guestToken;
    const response = await fetch(`${baseURL}/api/tickets/${ticketId}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!response.ok) throw new Error('Failed to download ticket PDF');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ticket-${ticketId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Categories API
export const categoriesAPI = {
  getCategories: () => api.get('/api/categories'),
  getCategory: (id) => api.get(`/api/categories/${id}`),
  createCategory: (data) => api.post('/api/categories', data),
  updateCategory: (id, data) => api.put(`/api/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/api/categories/${id}`),
};

// Venues API
export const venuesAPI = {
  getVenues: (params = {}) => api.get('/api/venues', { params }),
  getVenue: (id) => api.get(`/api/venues/${id}`),
  createVenue: (data) => api.post('/api/venues', data),
  updateVenue: (id, data) => api.put(`/api/venues/${id}`, data),
  deleteVenue: (id) => api.delete(`/api/venues/${id}`),
};

// Payments API
export const paymentsAPI = {
  getProvider: (params = {}) => api.get('/api/payments/provider', { params }),
  getPayment: (paymentId, config = {}) => api.get(`/api/payments/${paymentId}`, { timeout: 30000, ...config }),
  getPaymentStatus: (paymentId, options = {}) => {
    const params = {};
    if (options.query) params.query = '1';
    // NCBA status query can take longer than the default 10s client timeout
    return api.get(`/api/payments/${paymentId}/status`, {
      params,
      timeout: 45000,
      ...(options.headers ? { headers: options.headers } : {})
    });
  },
  initiatePayment: (data, config = {}) => api.post('/api/payments/initiate', data, { timeout: 60000, ...config }),
  getPaymentHistory: () => api.get('/api/payments/history'),
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/api/admin/dashboard'),
  getUsers: (params = {}) => api.get('/api/admin/users', { params }),
  createUser: (data) => api.post('/api/admin/users', data, { timeout: 30000 }),
  updateUser: (id, data) => api.put(`/api/admin/users/${id}`, data, { timeout: 30000 }),
  getEvents: (params = {}) => api.get('/api/admin/events', { params }),
  getEvent: (id) => api.get(`/api/admin/events/${id}`),
  createEvent: (data) => api.post('/api/admin/events', data),
  updateEvent: (id, data) => api.put(`/api/admin/events/${id}`, data),
  getUserAnalytics: (params = {}) => api.get('/api/admin/analytics/users', { params }),
  getRevenueAnalytics: (params = {}) => api.get('/api/admin/analytics/revenue', { params }),
  updateUserStatus: (id, data) => api.patch(`/api/admin/users/${id}/status`, data),
  deleteEvent: (id) => api.delete(`/api/admin/events/${id}`),
  restoreEvent: (id, data = { status: 'ACTIVE' }) => api.patch(`/api/admin/events/${id}/restore`, data),
  getSettings: (options = {}) => api.get('/api/admin/settings', options),
  updateSettings: (data) => api.put('/api/admin/settings', data),
  testSmtpEmail: (to) => api.post('/api/admin/settings/test-email', { to }),
  testMpesaConnection: () => api.post('/api/admin/settings/test-mpesa'),
  testNcbaConnection: () => api.post('/api/admin/settings/test-ncba'),
  generateNcbaCredentials: (data = {}) => api.post('/api/admin/settings/ncba/generate-credentials', data),
  revealNcbaCredentials: () => api.post('/api/admin/settings/ncba/reveal-credentials'),
  getPayments: (params = {}) => api.get('/api/admin/payments', { params }),
  queryPayment: (paymentId) => api.post(`/api/admin/payments/${paymentId}/query`),
  getFamilyBankIpnRecords: (params = {}) => api.get('/api/admin/family-bank/ipn-records', { params }),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  getCategories: (params = {}) => api.get('/api/admin/categories', { params }),
  createCategory: (data) => api.post('/api/admin/categories', data),
  updateCategory: (id, data) => api.put(`/api/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/api/admin/categories/${id}`),
  getTickets: (params = {}) => api.get('/api/admin/tickets', { params }),
  getFailedCheckouts: (params = {}) => api.get('/api/admin/failed-checkouts', { params }),
  getSalesSummary: (eventId) => api.get(`/api/admin/events/${eventId}/sales-summary`),
  issueComplimentaryTickets: (eventId, buyers) =>
    api.post(`/api/admin/events/${eventId}/complimentary`, { buyers }, { timeout: 120000 }),
  markTicketUsed: (ticketId, data = {}) => api.patch(`/api/admin/tickets/${ticketId}/use`, data),
  refundTicket: (ticketId, data = {}) => api.patch(`/api/admin/tickets/${ticketId}/refund`, data),
  emailTicket: (ticketId) =>
    api.post(`/api/admin/tickets/${ticketId}/email`, {}, { timeout: 90000 }),
  exportSalesExcel: async (eventId) => downloadAdminFile(`/api/admin/events/${eventId}/sales/export.xlsx`),
  exportSalesPdf: async (eventId) => downloadAdminFile(`/api/admin/events/${eventId}/sales/export.pdf`),
  getGateStaff: (eventId) => api.get(`/api/admin/events/${eventId}/gate-staff`),
  createGateStaff: (eventId, data) => api.post(`/api/admin/events/${eventId}/gate-staff`, data),
  removeGateStaff: (eventId, assignmentId) =>
    api.delete(`/api/admin/events/${eventId}/gate-staff/${assignmentId}`),
  resetGateStaffPassword: (eventId, assignmentId) =>
    api.post(`/api/admin/events/${eventId}/gate-staff/${assignmentId}/reset-password`),
  getOrganizerTeam: (params = {}) => api.get('/api/admin/organizer-team', { params }),
  createOrganizerTeamMember: (data) => api.post('/api/admin/organizer-team', data),
  removeOrganizerTeamMember: (memberId) => api.delete(`/api/admin/organizer-team/${memberId}`)
};

async function downloadAdminFile(path) {
  const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');
  const response = await fetch(`${baseURL}${path}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  if (!response.ok) {
    let message = 'Download failed';
    try {
      const data = await response.json();
      message = data.message || message;
    } catch {
      // not JSON
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `nikovibe-export-${Date.now()}`;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { filename };
}

/**
 * Download PostgreSQL backup as .sql (super admin only).
 */
export const downloadDatabaseBackup = async () => {
  const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  const response = await fetch(`${baseURL}/api/admin/database/backup`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  if (!response.ok) {
    let message = 'Failed to create database backup';
    try {
      const data = await response.json();
      message = data.message || message;
    } catch {
      // response body is not JSON
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `afro_tickets_backup_${Date.now()}.sql`;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  const savedPath = response.headers.get('X-Backup-Saved-As');

  return { filename, savedPath };
};

export const listDatabaseBackups = () => api.get('/api/admin/database/backups');

export const downloadSavedDatabaseBackup = async (filename) => {
  const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');
  const query = filename ? `?filename=${encodeURIComponent(filename)}` : '';

  const response = await fetch(`${baseURL}/api/admin/database/backups/download${query}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  if (!response.ok) {
    let message = 'Failed to download database backup';
    try {
      const data = await response.json();
      message = data.message || message;
    } catch {
      // not JSON
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const headerName = response.headers.get('X-Backup-Filename');
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const resolvedName = headerName || match?.[1] || `afro_tickets_backup_${Date.now()}.sql`;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = resolvedName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { filename: resolvedName };
};

/**
 * Start full site + database backup (runs in background on server).
 */
export const startSiteBackup = () => api.post('/api/admin/site-backup/start');

export const cancelSiteBackup = () => api.post('/api/admin/site-backup/cancel');

export const getSiteBackupStatus = () => api.get('/api/admin/site-backup/status');

export const downloadSiteBackup = async (filename) => {
  const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');
  const query = filename ? `?filename=${encodeURIComponent(filename)}` : '';

  const response = await fetch(`${baseURL}/api/admin/site-backup/download${query}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  if (!response.ok) {
    let message = 'Failed to download site backup';
    try {
      const data = await response.json();
      message = data.message || message;
    } catch {
      // not JSON
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const headerName = response.headers.get('X-Backup-Filename');
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const resolvedName = headerName || match?.[1] || `nikovibe_backup_${Date.now()}.zip`;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = resolvedName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { filename: resolvedName };
};

// Analytics API
export const analyticsAPI = {
  getPlatformAnalytics: () => api.get('/api/analytics/platform'),
  getEventAnalytics: (eventId) => api.get(`/api/analytics/events/${eventId}`),
  getOrganizerDashboard: () => api.get('/api/analytics/organizer/dashboard'),
  getRevenueAnalytics: (params = {}) => api.get('/api/analytics/revenue', { params }),
};

// Upload API
export const uploadAPI = {
  uploadEventImage: (file, meta = {}) => {
    const formData = new FormData();
    formData.append('image', file);
    if (meta.title) formData.append('title', meta.title);
    if (meta.description) formData.append('description', meta.description);
    return api.post('/api/upload/event-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadEventGallery: (files, meta = {}) => {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));
    if (meta.title) formData.append('title', meta.title);
    if (meta.description) formData.append('description', meta.description);
    return api.post('/api/upload/event-gallery', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/api/upload/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadVenueImages: (files, meta = {}) => {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));
    if (meta.title) formData.append('title', meta.title);
    if (meta.description) formData.append('description', meta.description);
    return api.post('/api/upload/venue-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteImage: (publicId) => api.delete(`/api/upload/image/${publicId}`),
  getUploadStats: () => api.get('/api/upload/stats'),
};

// Health API
export const healthAPI = {
  getHealth: () => api.get('/health'),
  getDetailedHealth: () => api.get('/health/detailed'),
  getStats: () => api.get('/health/stats'),
};

export default api;
